import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
    ArrowLeft, Loader2, Flag, Pencil, Ban, DollarSign, Plus, Send, Trash2, Receipt,
} from 'lucide-react';
import {
    useClaim, useClaims, useDeleteClaim, useUpdateClaim, useBillPR, deriveClaimFlags,
    type ClaimCOBSnapshot,
} from '@/api/claims';
import { useClient } from '@/api/clients';
import { useSettings, type PracticeSettings } from '@/api/settings';
import { useUserDirectory } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import {
    hasBlockingIssues,
    validate837P,
    type ValidationIssue,
} from '@/lib/validate837P';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';
import { ClaimEditDialog } from './ClaimEditDialog';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { CreateSecondaryDialog } from './CreateSecondaryDialog';
import { MarkSubmittedDialog } from './MarkSubmittedDialog';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
const POS_LABELS: Record<string, string> = {
    '02': 'Telehealth (02)',
    '10': 'Telehealth Patient Home (10)',
    '11': 'Office (11)',
    '12': 'Home (12)',
    '99': 'Other (99)',
};

function fmtMoney(n: number | null | undefined): string {
    if (n == null || isNaN(n as number)) return '—';
    return (n as number).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function fmtDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    return String(iso).slice(0, 10);
}

function fieldOrDash(v: string | null | undefined): string {
    return v && String(v).trim() ? String(v) : '—';
}

function formatServiceTime(hhmm: string | null | undefined): string {
    if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return '—';
    const [h, m] = hhmm.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDxPointers(p: 'A' | 'B' | 'AB' | string | null | undefined): string {
    if (p === 'AB') return 'A, B';
    return p || 'A';
}

export function BillingDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const canEdit = useAuthStore((s) => s.hasPermission('edit_billing'));

    const { data: claim, isLoading, isError, error } = useClaim(id);
    const { data: client } = useClient(claim?.clientId ?? undefined);
    const { data: settings } = useSettings();
    const { data: staffDirectory } = useUserDirectory();

    // We scope "primary claim lookup" via the already-cached claim list so we
    // can show the primary claim number on a secondary. If the cache is cold,
    // the primaryClaimId still renders — just without the display #.
    const { data: allClaims } = useClaims();
    const primaryClaim = useMemo(() => {
        if (!claim?.primaryClaimId || !allClaims) return null;
        return allClaims.find((c) => c.id === claim.primaryClaimId) ?? null;
    }, [claim?.primaryClaimId, allClaims]);

    // PR spillover: on a Payer claim we look for a non-voided spawn (child);
    // on a spawn claim we look for its source (parent). Either direction,
    // the counterpart links to its detail page so the biller can navigate
    // the PR chain without hunting by client.
    const prSpawn = useMemo(() => {
        if (!claim || !allClaims) return null;
        return allClaims.find((c) => c.prSourceClaimId === claim.id && c.status !== 'Voided') ?? null;
    }, [claim, allClaims]);
    const prSource = useMemo(() => {
        if (!claim?.prSourceClaimId || !allClaims) return null;
        return allClaims.find((c) => c.id === claim.prSourceClaimId) ?? null;
    }, [claim?.prSourceClaimId, allClaims]);

    const [editOpen, setEditOpen]             = useState(false);
    const [payOpen, setPayOpen]               = useState(false);
    const [secondaryOpen, setSecondaryOpen]   = useState(false);
    const [voidOpen, setVoidOpen]             = useState(false);
    const [deleteOpen, setDeleteOpen]         = useState(false);
    const [markSubmittedOpen, setMarkSubmittedOpen] = useState(false);

    const updateClaim = useUpdateClaim();
    const deleteClaim = useDeleteClaim();
    const billPR      = useBillPR();
    const [billPRError, setBillPRError] = useState<string | null>(null);

    if (isLoading) {
        return (
            <div className="p-6 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading claim…
            </div>
        );
    }
    if (isError || !claim) {
        return (
            <div className="p-6 max-w-[900px] mx-auto">
                <Link to="/billing" className="text-xs text-text-muted hover:text-text inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back to claims
                </Link>
                <div className="mt-4 p-6 text-center text-error">
                    <p className="font-medium mb-1">Couldn't load claim</p>
                    <p className="text-xs text-text-subtle">
                        {extractErrorMessage(error, 'It may have been deleted.')}
                    </p>
                </div>
            </div>
        );
    }

    const flags = claim.flags.length ? claim.flags : deriveClaimFlags(claim);

    // When the claim is a secondary, subscriber / member-id details come
    // from the client's ins2; otherwise ins1. Matches the legacy behavior.
    const insRec = claim.primaryClaimId ? (client?.ins2 ?? {}) : (client?.ins1 ?? {});

    // Resolve rendering provider via directory match on `staff` name.
    // Fragile (no FK yet) but adequate until Phase 2.5.2 adds claims.staff_id.
    const renderingStaff = (staffDirectory ?? []).find(
        (s) => `${s.first} ${s.last}`.trim() === (claim.staff ?? '').trim(),
    );

    // 837P validation is Electronic-only — Manual payers don't go through 837P.
    const validationIssues: ValidationIssue[] = claim.submissionMethod !== 'Manual' && client
        ? validate837P({
            claim,
            client,
            practice: settings?.practice,
            staff: renderingStaff,
        })
        : [];
    const blockedBy837P = hasBlockingIssues(validationIssues);

    const canVoid = canEdit && claim.status !== 'Voided';
    // "Mark submitted" applies to Manual-payer Ready claims only. Electronic
    // claims get status=Submitted indirectly via the 837P submission flow, so
    // no manual flip is exposed for them.
    const isManualPayer = claim.submissionMethod === 'Manual';
    const isElectronicPayer = claim.submissionMethod !== 'Manual';
    const canMarkSubmitted = isManualPayer && canEdit && claim.status === 'Ready';
    const canRecordPayment = canEdit && (claim.status === 'Submitted' || claim.status === 'Ready' || claim.status === 'Partial');
    const canCreateSecondary =
        canEdit
        && !claim.primaryClaimId
        && (claim.status === 'Paid' || claim.status === 'Partial');
    // Bill PR to client: only available on a Payer claim with a positive PR
    // balance, no existing non-voided spawn, and not itself a PR spawn.
    const canBillPR =
        canEdit
        && claim.billTo === 'Payer'
        && !claim.prSourceClaimId
        && claim.prBalance > 0
        && !prSpawn;

    async function handleVoidConfirm() {
        if (!canVoid || !claim) return;
        await updateClaim.mutateAsync({ id: claim.id, payload: { status: 'Voided' } });
        setVoidOpen(false);
    }

    async function handleDeleteConfirm() {
        if (!canEdit || !claim) return;
        await deleteClaim.mutateAsync(claim.id);
        navigate('/billing');
    }

    async function handleBillPR() {
        if (!canBillPR || !claim) return;
        setBillPRError(null);
        try {
            const spawn = await billPR.mutateAsync(claim.id);
            navigate(`/billing/${spawn.id}`);
        } catch (e) {
            const msg = e instanceof AxiosError
                ? (e.response?.data?.error ?? 'Failed to create PR claim')
                : 'Unexpected error';
            setBillPRError(msg);
        }
    }

    return (
        <div className="p-6 max-w-[1100px] mx-auto">
            <Link to="/billing" className="text-xs text-text-muted hover:text-text inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to claims
            </Link>

            <header className="mt-3 mb-5 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="font-display text-2xl font-bold text-text">{claim.claimNo}</h1>
                        <StatusBadge status={claim.status} />
                        {claim.primaryClaimId && (
                            <span className="inline-flex items-center px-2 py-0.5 text-2xs font-bold uppercase tracking-wider bg-surface-muted rounded-sm text-text-muted">
                                Secondary
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-text-subtle mt-1">
                        {claim.client}
                        {claim.serviceDate && ` · ${fmtDate(claim.serviceDate)}`}
                        {claim.payer && ` · ${claim.payer}`}
                    </p>
                </div>
                {canEdit && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
                            <Pencil className="w-3 h-3" /> Edit
                        </Button>
                        {canMarkSubmitted && (
                            <Button variant="ghost" size="sm" onClick={() => setMarkSubmittedOpen(true)}>
                                <Send className="w-3 h-3" /> Mark Submitted
                            </Button>
                        )}
                        {isElectronicPayer && (
                            <Button
                                variant="ghost"
                                size="sm"
                                disabled
                                title={
                                    blockedBy837P
                                        ? 'Resolve the pre-flight 837P issues before submitting.'
                                        : 'Submit from the Billing list — Electronic claims go out as an 837P batch.'
                                }
                            >
                                <Send className="w-3 h-3" /> Submit electronically
                            </Button>
                        )}
                        {canRecordPayment && (
                            <Button variant="ghost" size="sm" onClick={() => setPayOpen(true)}>
                                <DollarSign className="w-3 h-3" /> Record Payment
                            </Button>
                        )}
                        {canCreateSecondary && (
                            <Button variant="primary" size="sm" onClick={() => setSecondaryOpen(true)}>
                                <Plus className="w-3 h-3" /> Create Secondary
                            </Button>
                        )}
                        {canBillPR && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { void handleBillPR(); }}
                                disabled={billPR.isPending}
                            >
                                <Receipt className="w-3 h-3" /> {billPR.isPending ? 'Billing PR…' : 'Bill PR to client'}
                            </Button>
                        )}
                        {canVoid && (
                            <Button variant="ghost" size="sm" onClick={() => setVoidOpen(true)}>
                                <Ban className="w-3 h-3" /> Void
                            </Button>
                        )}
                        <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
                            <Trash2 className="w-3 h-3" /> Delete
                        </Button>
                    </div>
                )}
            </header>

            {flags.length > 0 && (
                <div className="mb-4 p-3 border border-[rgb(230_126_34_/_0.3)] bg-warning-soft rounded-md">
                    <div className="flex items-center gap-2 mb-1 text-warning-text text-xs font-medium">
                        <Flag className="w-3 h-3" /> {flags.length} validation flag{flags.length === 1 ? '' : 's'}
                    </div>
                    <ul className="text-xs text-text-muted list-disc pl-5 space-y-0.5">
                        {flags.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                </div>
            )}

            {isElectronicPayer && <Pre837PPanel issues={validationIssues} />}

            {billPRError && (
                <div className="mb-4 p-3 border border-error bg-error-soft rounded-md text-xs text-error">
                    {billPRError}
                </div>
            )}

            <Card className="p-5">
                <Section title="Claim">
                    <Field label="Claim #" value={claim.claimNo} mono />
                    <Field label="Status"       value={claim.status} />
                    <Field label="Service Date" value={fmtDate(claim.serviceDate)} />
                    <Field label="Date Created" value={fmtDate(claim.created)} />
                    {claim.voidedAt && <Field label="Voided"   value={fmtDate(claim.voidedAt)} />}
                </Section>

                <Section title="Billing Provider">
                    <Field label="Practice Name" value={settings?.practice.practiceName} />
                    <Field label="Billing NPI"   value={settings?.practice.npi} />
                    <Field label="Tax ID (EIN)"  value={settings?.practice.ein} />
                    <Field label="Address"       value={formatPracticeAddress(settings?.practice)} />
                    <Field label="Taxonomy Code" value={settings?.practice.taxonomy} />
                </Section>

                <Section title="Rendering Provider">
                    <Field label="Provider Name" value={claim.staff} />
                    <Field label="NPI"           value={renderingStaff?.npi} />
                    <Field label="Taxonomy Code" value={renderingStaff?.taxonomy} />
                </Section>

                <Section title="Patient / Subscriber">
                    <Field label="Patient Name"            value={claim.client} />
                    <Field label="Date of Birth"           value={fmtDate(client?.dob)} />
                    <Field label="Gender"                  value={client?.gender} />
                    <Field label="Patient Address"         value={formatClientAddress(client)} span={2} />
                    <Field label="Member ID"               value={insRec.id} />
                    <Field label="Group Number"            value={insRec.group} />
                    <Field label="Subscriber Relationship" value={formatRelationship(insRec.subRel, client?.grel)} />
                    <Field label="Subscriber"              value={insRec.sub || (insRec.subRel && insRec.subRel !== 'self' ? client?.gname : claim.client)} />
                    {insRec.subRel && insRec.subRel !== 'self' && (
                        <>
                            <Field label="Subscriber DOB"     value={fmtDate(insRec.subDob)} />
                            <Field label="Subscriber Gender"  value={insRec.subGender} />
                            <Field
                                label="Subscriber Address"
                                value={formatSubAddress(insRec.subAddress)}
                                span={2}
                            />
                        </>
                    )}
                </Section>

                <Section title="Payer">
                    <Field label="Payer Name"     value={claim.payer} />
                    <Field label="Payer ID (EDI)" value={claim.payerId} />
                    <Field
                        label="Submission Method"
                        value={claim.submissionMethod ?? '—'}
                    />
                    {claim.submissionMethod === 'Manual' && claim.submittedAt && (
                        <>
                            <Field label="Submitted On"        value={fmtDate(claim.submittedAt)} />
                            <Field label="Confirmation #"      value={claim.submitConfirmation || '—'} />
                        </>
                    )}
                </Section>

                <Section title="Service Line">
                    <Field label="CPT Code"         value={claim.serviceCode} />
                    <Field
                        label="Modifiers"
                        value={[claim.mod1, claim.mod2, claim.mod3, claim.mod4].filter(Boolean).join(', ')}
                    />
                    <Field label="Place of Service" value={POS_LABELS[claim.pos] ?? claim.pos} />
                    <Field label="Start Time"       value={formatServiceTime(claim.serviceStartTime)} />
                    <Field label="End Time"         value={formatServiceTime(claim.serviceEndTime)} />
                    <Field label="Units"            value={String(claim.units)} />
                    <Field label="Unit Rate"        value={fmtMoney(claim.unitRate)} />
                    <Field label="Billed Amount"    value={fmtMoney(claim.amount)} />
                    <Field label="Allowed Amount"   value={claim.allowedAmount ? fmtMoney(claim.allowedAmount) : '—'} />
                    <Field label="Diagnosis Pointer" value={formatDxPointers(claim.dxPointers)} />
                </Section>

                <Section title="Diagnosis (ICD-10)">
                    <Field label="Primary (A)"      value={claim.dx1 || client?.dx1Code} />
                    <Field label="Secondary (B)"    value={claim.dx2 || client?.dx2Code} />
                </Section>

                <Section title="Authorization / Referral">
                    <Field label="Authorization # (REF*G1)" value={claim.authNumber} />
                    <Field label="Referral # (REF*9F)"      value={claim.referralNumber} />
                </Section>

                <Section title="Claim Frequency">
                    <Field label="Frequency" value={formatFrequency(claim.frequency)} />
                    {claim.frequency !== 'original' && (
                        <Field label="Original Claim # (REF*F8)" value={claim.originalClaimNumber} />
                    )}
                </Section>

                <Section title="Payment">
                    <Field label="Insurance Paid" value={claim.paidAmount ? fmtMoney(claim.paidAmount) : '—'} />
                    <Field
                        label="PR Balance"
                        value={claim.prBalance ? fmtMoney(claim.prBalance) : '—'}
                        valueClass={claim.prBalance ? 'text-error font-bold' : ''}
                    />
                    <Field label="Payment Date"     value={fmtDate(claim.paymentDate)} />
                    <Field label="Payer Reference #" value={claim.payerRef} />
                </Section>

                {(prSpawn || prSource) && (
                    <Section title="Patient Responsibility">
                        {prSpawn && (
                            <Field
                                label="PR billed to client"
                                valueNode={
                                    <Link to={`/billing/${prSpawn.id}`} className="text-text hover:underline">
                                        {prSpawn.claimNo} · {fmtMoney(prSpawn.amount)}
                                    </Link>
                                }
                                value={prSpawn.claimNo}
                                span={2}
                            />
                        )}
                        {prSource && (
                            <Field
                                label="Spawned from payer claim"
                                valueNode={
                                    <Link to={`/billing/${prSource.id}`} className="text-text hover:underline">
                                        {prSource.claimNo}
                                    </Link>
                                }
                                value={prSource.claimNo}
                                span={2}
                            />
                        )}
                    </Section>
                )}

                {(claim.denialReason || claim.status === 'Denied') && (
                    <Section title="Denial / Appeal">
                        <Field label="Denial Reason" value={claim.denialReason} span={2} />
                    </Section>
                )}

                {claim.primaryClaimId && claim.cob && (
                    <Section title="Coordination of Benefits (COB)">
                        <Field
                            label="Primary Claim #"
                            value={primaryClaim ? primaryClaim.claimNo : 'Load claims to see claim #'}
                            valueNode={
                                primaryClaim && (
                                    <Link to={`/billing/${primaryClaim.id}`} className="text-text hover:underline">
                                        {primaryClaim.claimNo}
                                    </Link>
                                )
                            }
                        />
                        <CobSnapshot cob={claim.cob} />
                    </Section>
                )}

                {claim.notes && (
                    <Section title="Notes">
                        <Field label="Notes" value={claim.notes} span={2} />
                    </Section>
                )}
            </Card>

            {canEdit && (
                <>
                    <ClaimEditDialog
                        claim={claim}
                        open={editOpen}
                        onOpenChange={setEditOpen}
                    />
                    <RecordPaymentDialog
                        claim={claim}
                        open={payOpen}
                        onOpenChange={setPayOpen}
                    />
                    <CreateSecondaryDialog
                        claim={claim}
                        secondaryPayerName={client?.ins2?.payer ?? ''}
                        open={secondaryOpen}
                        onOpenChange={setSecondaryOpen}
                    />
                    <MarkSubmittedDialog
                        claims={[claim]}
                        open={markSubmittedOpen}
                        onOpenChange={setMarkSubmittedOpen}
                    />
                    <ConfirmDialog
                        open={voidOpen}
                        onOpenChange={setVoidOpen}
                        title="Void claim?"
                        message={
                            <>
                                Mark claim <span className="font-medium text-text">{claim.claimNo}</span> as
                                voided? Payment, allowed, and PR amounts are kept for history.
                            </>
                        }
                        confirmLabel="Void claim"
                        confirmVariant="danger"
                        pending={updateClaim.isPending}
                        onConfirm={handleVoidConfirm}
                    />
                    <ConfirmDialog
                        open={deleteOpen}
                        onOpenChange={setDeleteOpen}
                        title="Delete claim?"
                        message={
                            <>
                                Delete claim <span className="font-medium text-text">{claim.claimNo}</span>{' '}
                                entirely? This cannot be undone. If you need to keep history for reporting,
                                void the claim instead.
                            </>
                        }
                        confirmLabel="Delete claim"
                        confirmVariant="danger"
                        pending={deleteClaim.isPending}
                        onConfirm={handleDeleteConfirm}
                    />
                </>
            )}
        </div>
    );
}

function ConfirmDialog({
    open, onOpenChange, title, message, confirmLabel, confirmVariant = 'danger', pending, onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    confirmVariant?: 'danger' | 'primary';
    pending?: boolean;
    onConfirm: () => void | Promise<void>;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">{message}</p>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button" disabled={pending}>Cancel</Button>
                    </DialogClose>
                    <Button
                        variant={confirmVariant}
                        size="sm"
                        disabled={pending}
                        onClick={() => { void onConfirm(); }}
                    >
                        {pending ? 'Working…' : confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="mb-5 last:mb-0">
            <h2 className="text-2xs uppercase tracking-wider font-bold text-text-subtle border-b border-border pb-1 mb-3">
                {title}
            </h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
        </section>
    );
}

function Field({
    label, value, valueNode, mono, span, valueClass,
}: {
    label: string;
    value?: string | null;
    valueNode?: React.ReactNode;
    mono?: boolean;
    span?: 1 | 2;
    valueClass?: string;
}) {
    return (
        <div className={cn(span === 2 && 'col-span-2')}>
            <div className="text-2xs uppercase tracking-wider text-text-subtle mb-0.5">{label}</div>
            <div className={cn('text-sm text-text', mono && 'font-mono', valueClass)}>
                {valueNode ?? fieldOrDash(value)}
            </div>
        </div>
    );
}

function CobSnapshot({ cob }: { cob: ClaimCOBSnapshot }) {
    return (
        <>
            <Field label="Primary Payer"    value={cob.primaryPayer} />
            <Field label="Primary Billed"   value={fmtMoney(cob.primaryBilled)} />
            <Field label="Primary Allowed"  value={fmtMoney(cob.primaryAllowed)} />
            <Field label="Primary Paid"     value={fmtMoney(cob.primaryPaid)} />
            <Field label="Primary PR"       value={fmtMoney(cob.primaryPR)} />
            <Field label="Primary Adj"      value={fmtMoney(cob.primaryAdjustmentAmt)} />
            <Field label="Primary Payer Ref" value={cob.primaryPayerRef} />
            <Field label="Primary Payment Date" value={fmtDate(cob.primaryPaymentDate)} />
        </>
    );
}

function Pre837PPanel({ issues }: { issues: ValidationIssue[] }) {
    if (issues.length === 0) return null;
    const blocking = issues.filter((i) => i.severity === 'blocking');
    const warnings = issues.filter((i) => i.severity === 'warning');
    const hasBlocking = blocking.length > 0;
    return (
        <div
            className={cn(
                'mb-4 p-3 rounded-md border',
                hasBlocking
                    ? 'border-error bg-error-soft'
                    : 'border-[rgb(230_126_34_/_0.3)] bg-warning-soft',
            )}
        >
            <div className={cn(
                'flex items-center gap-2 mb-1 text-xs font-medium',
                hasBlocking ? 'text-error' : 'text-warning-text',
            )}>
                <Flag className="w-3 h-3" />
                837P pre-flight —{' '}
                {hasBlocking
                    ? `${blocking.length} blocking issue${blocking.length === 1 ? '' : 's'}`
                    : `${warnings.length} warning${warnings.length === 1 ? '' : 's'}`}
            </div>
            <ul className="text-xs text-text-muted list-disc pl-5 space-y-0.5">
                {blocking.map((i, idx) => (
                    <li key={`b${idx}`}>
                        <span className="text-error font-medium">Block:</span> {i.message}
                    </li>
                ))}
                {warnings.map((i, idx) => (
                    <li key={`w${idx}`}>
                        <span className="text-warning-text">Warn:</span> {i.message}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function formatRelationship(
    rel: 'self' | 'spouse' | 'child' | 'other' | undefined,
    fallback: string | undefined,
): string {
    if (!rel || rel === 'self') return rel === 'self' ? 'Self' : (fallback || '');
    return rel.charAt(0).toUpperCase() + rel.slice(1);
}

function formatSubAddress(a: { street?: string; city?: string; state?: string; zip?: string } | undefined): string {
    if (!a) return '';
    return [a.street, [a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean).join(', ');
}

function formatFrequency(f: string): string {
    if (f === 'corrected') return 'Corrected (7)';
    if (f === 'void')      return 'Void (8)';
    return 'Original (1)';
}

function formatPracticeAddress(p: PracticeSettings | undefined): string {
    if (!p) return '';
    const line = [p.address, [p.city, p.state].filter(Boolean).join(', '), p.zip].filter(Boolean).join(', ');
    return line;
}

type AddressBearing = { address?: string; city?: string; state?: string; zip?: string };
function formatClientAddress(c: AddressBearing | undefined | null): string {
    if (!c) return '';
    return [c.address, [c.city, c.state].filter(Boolean).join(', '), c.zip].filter(Boolean).join(', ');
}
